import {
  closePicker,
  closeViewer,
  editor,
  picker,
  reader,
  selection,
  detail,
  view,
  viewer,
  type View,
} from './state.js';

/**
 * **L'historique du navigateur** — ce qui manquait pour que le bouton *Retour*
 * existe.
 *
 * Le site était une application à une seule entrée d'historique : `view` était un
 * signal, et rien ne l'écrivait dans `history`. Trois conséquences, toutes
 * relevées à l'usage sur un téléphone :
 *
 * ① **Ouvrir un groupe était un aller sans retour.** Le geste système du retour —
 *    le seul que tout le monde connaisse sur Android — quittait le site au lieu de
 *    revenir à la liste des groupes ; et aucun bouton ne le remplaçait.
 * ② **Une surcouche ne se fermait qu'au bouton.** Visionneuse, lecteur, éditeur,
 *    fiche : le retour système les traversait toutes d'un coup, en sortant.
 * ③ **Un lien vers une page interne n'existait pas.** L'URL restait `/` quoi qu'on
 *    regarde — ce point-là reste ouvert, et il est traité plus bas.
 *
 * ### Le modèle : une entrée par *endroit*, une entrée par *surcouche*
 *
 * Chaque entrée d'historique porte un instantané `{ view, overlay }`. Ouvrir un
 * groupe empile un instantané de vue ; ouvrir la visionneuse empile un instantané
 * de **même vue** avec une surcouche. Le retour dépile, et l'application se remet
 * dans l'état décrit par l'entrée où l'on atterrit — jamais dans l'état qu'elle
 * croit se rappeler.
 *
 * **Le retour avant est volontairement partiel** : revenir en avant sur une
 * surcouche ne la rouvre pas. Rouvrir une visionneuse demanderait de reconstituer
 * la liste qu'elle parcourait, laquelle est figée à l'ouverture par construction
 * (`openViewer`) ; la restaurer depuis l'historique produirait une liste
 * plausible et fausse. Fermer est exact, rouvrir ne le serait pas.
 *
 * ### Pourquoi l'URL ne change pas
 *
 * `pushState` est appelé **sans URL**, donc l'adresse reste `/`. C'est délibéré à
 * ce stade : l'aiguillage d'amorçage (`main.tsx`) traite `/callback` et rien
 * d'autre, et l'hébergement rend `index.html` pour tout chemin — une URL
 * `/groupes/01J…` marcherait donc au rechargement, mais **le mémo d'espace**
 * (`synkara.space`) et elle se contrediraient au démarrage. Nommer les vues dans
 * l'URL est un incrément à soi, pas un effet de bord de celui-ci.
 *
 * **Une seule adresse est lue** *(L1, 2026-09-14)* : celle d'un lien,
 * `/partage/{aid}/{fid}`. Elle ne nomme pas une vue, elle déclenche un geste —
 * `main.tsx` la met de côté au démarrage et rend `/` à la barre d'adresse
 * (`link.ts`). Le modèle ci-dessus n'en est pas changé.
 */

/** Les surcouches qui méritent une entrée d'historique — celles qui *couvrent*. */
export type Overlay = 'viewer' | 'reader' | 'editor' | 'picker' | 'inspector';

interface Snapshot {
  /** Le marqueur qui distingue nos entrées de celles d'un autre code. */
  readonly synkara: true;
  readonly view: View;
  readonly overlay?: Overlay;
}

interface Handlers {
  /** Applique une vue **sans rien empiler** — le pendant d'`openView`. */
  readonly applyView: (next: View) => void;
  /** Ferme l'éditeur en enregistrant : une sortie ne doit pas jeter la frappe. */
  readonly closeEditor: () => void;
}

let handlers: Handlers | undefined;

/** L'instantané de l'entrée où l'on se trouve, tenu en parallèle de `history`. */
let current: Snapshot | undefined;

const snapshotOf = (state: unknown): Snapshot | undefined =>
  typeof state === 'object' && state !== null && (state as Snapshot).synkara === true
    ? (state as Snapshot)
    : undefined;

const sameView = (a: View, b: View): boolean => JSON.stringify(a) === JSON.stringify(b);

/**
 * Installe le pont. Appelé une fois, à l'amorçage : l'entrée d'origine est
 * **remplacée** et non empilée, sinon le premier retour ne mènerait nulle part
 * tout en restant sur le site.
 */
export function installHistory(next: Handlers): void {
  handlers = next;
  current = { synkara: true, view: view.peek() };
  history.replaceState(current, '');
  window.addEventListener('popstate', onPopState);
}

function onPopState(event: PopStateEvent): void {
  const target = snapshotOf(event.state);
  // Une entrée qui n'est pas la nôtre — un `replaceState` d'amorçage, une
  // extension — ne doit pas faire deviner : on referme ce qui couvre, et on
  // laisse la vue où elle est.
  if (!target) {
    closeOverlays(undefined);
    current = { synkara: true, view: view.peek() };
    history.replaceState(current, '');
    return;
  }

  current = target;
  closeOverlays(target.overlay);
  if (!sameView(view.peek(), target.view)) handlers?.applyView(target.view);
}

/**
 * Referme **tout ce qui couvre**, sauf la surcouche que l'entrée visée décrit.
 *
 * L'ordre n'a pas d'importance, mais l'exhaustivité si : une surcouche oubliée
 * ici resterait montée par-dessus une vue qui, elle, aurait changé — l'état le
 * plus déroutant que puisse produire un retour.
 */
function closeOverlays(keep: Overlay | undefined): void {
  if (keep !== 'viewer' && viewer.peek() !== undefined) closeViewer();
  if (keep !== 'reader' && reader.peek() !== undefined) reader.value = undefined;
  if (keep !== 'editor' && editor.peek() !== undefined) handlers?.closeEditor();
  if (keep !== 'picker' && picker.peek() !== undefined) closePicker();
  if (keep !== 'inspector' && selection.peek() !== undefined) {
    selection.value = undefined;
    detail.value = undefined;
  }
}

/** Empile un endroit. Le même endroit deux fois de suite n'empile rien. */
export function pushView(next: View): void {
  if (!handlers) return;
  if (current && current.overlay === undefined && sameView(current.view, next)) return;
  current = { synkara: true, view: next };
  history.pushState(current, '');
}

/**
 * Remplace l'endroit courant, sans empiler. Réservé aux **corrections de
 * position** — le dossier qu'on regardait vient d'être supprimé, ici ou ailleurs :
 * on ne veut pas qu'un retour y ramène, il n'existe plus.
 */
export function replaceView(next: View): void {
  if (!handlers) return;
  current = { synkara: true, view: next };
  history.replaceState(current, '');
}

/** Empile une surcouche : le retour la refermera, et rien d'autre. */
export function pushOverlay(name: Overlay): void {
  if (!handlers) return;
  if (current?.overlay === name) return;
  current = { synkara: true, view: view.peek(), overlay: name };
  history.pushState(current, '');
}

/**
 * Ferme une surcouche **par l'historique quand c'est elle qui y est inscrite**.
 *
 * Sans ce détour, la croix et le geste système divergeraient : fermer au bouton
 * laisserait dans la pile une entrée décrivant une surcouche ouverte, et le
 * retour suivant se contenterait de la « refermer » — c'est-à-dire de ne rien
 * faire de visible, en avalant une pression.
 */
export function dismissOverlay(name: Overlay, close: () => void): void {
  if (handlers && current?.overlay === name) {
    history.back();
    return;
  }
  close();
}
